import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

const waLink = (e) => {
  const num = (e.whatsapp || e.phone || "").replace(/\D/g, "").replace(/^0/, "");
  const items = (e.items || []).map(i => `• ${i.product_name} × ${i.quantity}`).join("\n");
  const msg = `Hello ${e.customer_name}, this is REXTECH IT SOLUTION regarding your enquiry ${e.enquiry_id}:\n${items}\n\nWe will share availability and wholesale pricing shortly.`;
  return `https://wa.me/${num.length === 10 ? "91" + num : num}?text=${encodeURIComponent(msg)}`;
};

const STATUSES = ["new", "contacted", "quotation_sent", "negotiation", "confirmed", "completed", "cancelled"];

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/enquiries").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const update = async (patch) => { await api.patch(`/enquiries/${sel.id}`, patch); toast.success("Updated"); load(); setSel(s => ({...s, ...patch})); };
  return (
    <div>
      <h1 className="font-heading font-extrabold text-2xl mb-6">Enquiries</h1>
      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left"><tr><th className="p-3">ID</th><th>Customer</th><th>Company</th><th>Phone</th><th>Items</th><th>Status</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} className="border-t border-[#E2E8F0]" data-testid={`admin-enquiry-${e.id}`}>
                <td className="p-3 font-mono text-xs text-[#EA580C]">{e.enquiry_id}</td>
                <td>{e.customer_name}</td>
                <td>{e.company_name || "-"}</td>
                <td>{e.phone}</td>
                <td>{(e.items || []).length}</td>
                <td><Badge>{e.status}</Badge></td>
                <td className="text-xs text-slate-500">{e.created_at?.slice(0, 10)}</td>
                <td className="whitespace-nowrap pr-2">
                  <a href={waLink(e)} target="_blank" rel="noreferrer" data-testid={`enquiry-whatsapp-${e.id}`}><Button size="sm" className="bg-[#25D366] hover:bg-[#1EBE5A] text-white mr-1"><MessageCircle className="w-4 h-4"/></Button></a>
                  <Button size="sm" variant="outline" onClick={()=>setSel(e)}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!sel} onOpenChange={o=>!o && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          {sel && <>
            <DialogHeader><DialogTitle>Enquiry {sel.enquiry_id}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><b>Customer:</b> {sel.customer_name}</div>
                <div><b>Company:</b> {sel.company_name || "-"}</div>
                <div><b>Phone:</b> {sel.phone}</div>
                <div><b>WhatsApp:</b> {sel.whatsapp || "-"}</div>
                <div><b>Email:</b> {sel.email || "-"}</div>
                <div><b>Preferred:</b> {sel.preferred_contact}</div>
              </div>
              <a href={waLink(sel)} target="_blank" rel="noreferrer" data-testid="enquiry-whatsapp-reply"><Button className="bg-[#25D366] hover:bg-[#1EBE5A] text-white w-full"><MessageCircle className="w-4 h-4 mr-1"/> Reply on WhatsApp</Button></a>
              <div><b>Requirements:</b> <div className="text-sm text-slate-700">{sel.requirements || "-"}</div></div>
              <div><b>Items:</b>
                <ul className="list-disc pl-5 text-sm mt-1">
                  {(sel.items||[]).map((it,i)=><li key={`${it.product_id || it.product_name}-${i}`}>{it.product_name} — Qty: {it.quantity}</li>)}
                </ul>
              </div>
              <div>
                <label className="text-sm font-semibold">Status</label>
                <Select value={sel.status} onValueChange={v=>update({status: v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold">Admin Notes</label>
                <Textarea rows={3} value={sel.admin_notes||""} onChange={e=>setSel({...sel, admin_notes: e.target.value})} onBlur={()=>update({admin_notes: sel.admin_notes})}/>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
